/**
 * POS Product Selector Component
 *
 * Allows selecting multiple POS products to associate with a recipe.
 * Only shows products without an active recipe.
 */

import { useState, useRef, useEffect } from 'react';
import { Search, Package, X, Plus } from 'lucide-react';
import { useDebounce } from '@/hooks';
import { usePosProductsWithoutRecipe } from '../../hooks';
import type { PosProductWithoutRecipe, RecipePosProduct } from '@bstock/shared';

interface PosProductSelectorProps {
  /** Selected products with details (for display and filtering) */
  selectedProducts?: RecipePosProduct[];
  /** Called when a product is added */
  onAddProduct: (product: PosProductWithoutRecipe) => void;
  /** Called when a product is removed */
  onRemoveProduct: (productId: string) => void;
}

export function PosProductSelector({
  selectedProducts = [],
  onAddProduct,
  onRemoveProduct,
}: PosProductSelectorProps) {
  const selectedIds = selectedProducts.map(p => p.id);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(searchValue, 300);

  // Fetch POS products without recipes
  const { data: products, isLoading } = usePosProductsWithoutRecipe();

  // Filter products based on search and exclude already selected
  const filteredProducts = products?.filter((p) => {
    // Exclude already selected products
    if (selectedIds.includes(p.id)) return false;

    // Filter by search
    if (!debouncedSearch) return true;
    const search = debouncedSearch.toLowerCase();
    return (
      p.name.toLowerCase().includes(search) ||
      p.posId.toLowerCase().includes(search) ||
      (p.posCategory?.toLowerCase().includes(search) ?? false)
    );
  }) ?? [];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (product: PosProductWithoutRecipe) => {
    onAddProduct(product);
    setSearchValue('');
    setIsOpen(false);
  };

  const handleRemove = (productId: string) => {
    onRemoveProduct(productId);
  };

  return (
    <div className="space-y-3">
      {/* Selected Products List */}
      {selectedProducts.length > 0 && (
        <div className="space-y-2">
          {selectedProducts.map((product) => (
            <div
              key={product.id}
              className="flex items-center gap-2 px-3 py-2.5 bg-primary-50 border border-primary-200 rounded-lg"
            >
              <Package className="w-4 h-4 text-primary-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-dark-500 truncate block">
                  {product.name}
                </span>
                {product.posCategory && (
                  <span className="text-xs text-neutral-500">{product.posCategory}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemove(product.id)}
                className="p-1 text-neutral-400 hover:text-danger-500 hover:bg-white rounded transition-all"
                title="Retirer l'association"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={
              selectedProducts.length > 0
                ? 'Ajouter un autre produit POS...'
                : 'Rechercher un produit POS...'
            }
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm
                       placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500
                       focus:border-transparent transition-all"
          />
        </div>

        {/* Dropdown Results */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200
                       rounded-lg shadow-lg max-h-64 overflow-y-auto z-20"
          >
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-neutral-500 text-center">
                Chargement...
              </div>
            ) : filteredProducts.length > 0 ? (
              <ul>
                {filteredProducts.slice(0, 10).map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(product)}
                      className="w-full px-4 py-3 text-left hover:bg-neutral-50 transition-colors
                                 flex items-center gap-3 group"
                    >
                      <div className="w-8 h-8 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package className="w-4 h-4 text-neutral-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-dark-500 truncate">
                          {product.name}
                        </div>
                        <div className="text-xs text-neutral-400 truncate">
                          {product.posCategory || product.posId}
                        </div>
                      </div>
                      <Plus className="w-4 h-4 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-3 text-sm text-neutral-500 text-center">
                {searchValue
                  ? 'Aucun produit trouve'
                  : 'Tous les produits ont deja une recette'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Empty State */}
      {selectedProducts.length === 0 && !isOpen && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-neutral-50 border border-dashed border-neutral-300 rounded-lg">
          <Package className="w-4 h-4 text-neutral-400 flex-shrink-0" />
          <span className="text-sm text-neutral-500">
            Aucun produit fini associe
          </span>
        </div>
      )}
    </div>
  );
}
